import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import { operationType } from '../../../../../services/types';
import { Loader, Main, Styled } from './style';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { ExpandedBlock } from '../../../expanded';
import CopyToClipboard from 'react-copy-to-clipboard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faClipboard, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { webURL } from '../../../../../services/API';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

type slackTriggerProps = NodeProps<operationType> & {
    setChildMinimize?: (fn: (id: string) => void) => void;
    setChildSettings?: (fn: (id: string) => void) => void;
};

export function SlackTrigger({ id, data, setChildMinimize, setChildSettings }: slackTriggerProps) {
    const { setNodes } = useReactFlow();
    const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [isCopy, setIsCopy] = React.useState<boolean>(false);
    const [link, setLink] = React.useState<string>('');
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [searchParams] = useSearchParams();
    const projectId = searchParams.get('id');

    React.useEffect(() => {
        if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
        if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setChildMinimize, setChildSettings]);

    // 1 Automatically load link when expanded
    React.useEffect(() => {
        if (link === '') {
            setIsLoading(true);
            setTimeout(() => {
                if (isExpanded) {
                    retrieveLink();
                }
                setIsLoading(false);
            }, 6000);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isExpanded, data?.triggerCredentials?.webhookUrl]);

    // 2 Update retrieveLink()
    const retrieveLink = async () => {
        if (data?.triggerCredentials) {
            setLink(data.triggerCredentials.webhookUrl); //  Load from frontend state
        } else {
            await generateLink(); // First time: generate and store
        }
    };

    // 3 Update generateLink()
    const generateLink = async () => {
        try {
            const response = await axios.post(`${webURL}/triggers/slack/generate-link`, {
                nodeIndex: data.index,
                projectId
            });

            const { nodeId, nodeSecret, webhookUrl } = response.data;

            //  Save to frontend state
            setNodes(prev =>
                prev.map(node =>
                    node.data.index === data.index
                        ? {
                            ...node,
                            data: {
                                ...node.data,
                                triggerCredentials: { nodeId, nodeSecret, webhookUrl }
                            }
                        }
                        : node
                )
            );

            setLink(webhookUrl);

        } catch (error) {
            console.error(error);
        }
    };

    // const link = `${webURL}/blocks/slack-webhook`;
    const isWarning = false;

    return (<>
        <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
        <Main>
            {isWarning ? <>
               <div id="warning"></div>
               <span id="warningIcon"><IoWarningOutline size={21} /></span>
            </> : data.output ? <>
               <div id="ran"></div>
               <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
            </> : null}
            {isExpanded ? <ExpandedBlock
               data={{ id, ...data }}
               handleClose={() => setIsExpanded(false)}
               isWarning={isWarning}
               style={{ minWidth: '380px' }}
            >
               {isLoading ? <Loader /> : <Styled>
                  {/* Webhook URL Section */}
                  <div className="webhook-section">
                        <h4>Slack Webhook URL</h4>
                        <CopyToClipboard
                           text={link}
                           onCopy={_ => {
                              setIsCopy(true);
                              setTimeout(() => setIsCopy(false), 500);
                           }}
                        >
                           <div className="link-container" title={link}>
                              <div className="link-with-copy">
                                    <FontAwesomeIcon
                                       icon={isCopy ? faCheck : faClipboard}
                                       className={`copy-icon ${isCopy ? 'copied' : ''}`}
                                    />
                                    <span className="link-text">{link}</span>
                              </div>
                           </div>
                        </CopyToClipboard>

                  </div>

                  {/* Instructions Section */}
                  <div className="instructions-section">
                        <h5>Setup Instructions:</h5>
                        <ol className="steps-list">
                           <li>
                              <a
                                 href="https://api.slack.com/apps"
                                 target="_blank"
                                 rel="noopener noreferrer"
                                 className="external-link"
                              >
                                 Go to Slack API Apps
                                 <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
                              </a>
                              {"  "}to create or select your app.
                           </li>
                           <li>Navigate to <strong>Features</strong> → <i>Event Subscriptions</i> on the left sidebar.</li>
                           <li>Toogle <strong>Enable Events</strong> and add the webhook link above in <i>Request Url</i>.</li>
                           <li>Under <strong>Subscribe to bot events</strong> click <strong>Add Bot User Event</strong> and add your perferred events. <i>For example: "app_mention"</i>.</li>
                           <li>Click on <strong>Save Changes</strong>.</li>
                           <li>Navigate to <strong>Features</strong> → <i>OAuth & Permissions</i> on the left sidebar.</li>
                           {/* <li>Under <strong>Scopes</strong> and <strong>Bot Token Scopes</strong> add your perferred scopes. <i>For example: "channels: history" and "im:history"</i>.</li> */}
                           <li>Under <strong>OAuth Tokens</strong> click "Install to <i>your workspace name".</i></li>
                           <li><strong>In Slack</strong>, open the workspace menu (three dots, top right), choose Integrations, and add your app to the workspace.</li>
                        </ol>
                  </div>
               </Styled>}
            </ExpandedBlock> : null}
        </Main>
    </>);
}